import { ArrowRight, Boxes, Workflow } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { useProject } from "@/api/projects";
import { ApiError } from "@/api/client";
import { Card, CardDescription, CardHeader, CardTitle, cardVariants } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * The two choices this page offers, as data (flowstack#1397).
 *
 * `label` is the link's ACCESSIBLE NAME, spelled out rather than left to the
 * accessible-name-from-contents computation over a paragraph of prose. The
 * operator's report was that a screen reader announced "link" here while every
 * other link on the page announced its destination; the fix that cannot regress
 * is an explicit name, and the visible title stays the first line of the card so
 * the two never disagree.
 */
const CHOICES = [
  {
    slugPath: "tile",
    Icon: Boxes,
    title: "Submit a Tile",
    /** Announced name — reads as a destination, not as a paragraph. */
    label: "Submit a Tile — one containerized step",
    tagline: "One containerized step.",
    body: (
      <>
        Pick from the catalog (e.g. <span className="font-mono">affdo.amber_topology</span>,{" "}
        <span className="font-mono">amberflow.binding_pose_refinement</span>) and supply its
        declared inputs and parameters. Best when you need exactly one computation — running a quick
        force-field assembly or refining a single pose.
      </>
    ),
    cost: "Fast: minutes to a few hours per tile.",
  },
  {
    slugPath: "flow",
    Icon: Workflow,
    title: "Submit a Flow",
    label: "Submit a Flow — the end-to-end AFFDO pipeline",
    tagline: "End-to-end AFFDO pipeline.",
    body: (
      <>
        Pick a Flow template (<span className="font-mono">full_affdo_sequential</span> or{" "}
        <span className="font-mono">full_affdo_wavefront</span>) and supply the ligand Molecule. The
        runner expands the template into per-fragment + per-torsion children and produces a
        ForceFieldBundle v1.1.
      </>
    ),
    cost: "Slow: hours to days. Sequential trades walltime for per-scan-point visibility; wavefront is faster overall when GPU resources are scarce.",
  },
] as const;

export function SubmitPickerPage() {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);

  if (project.isLoading) {
    return (
      <div role="status" aria-live="polite" className="p-6 text-sm text-muted-foreground">
        Loading project…
      </div>
    );
  }

  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <Card elevation="raised">
        <CardHeader>
          <CardTitle>{isNotFound ? "Project not found" : "Could not load project"}</CardTitle>
          <CardDescription>
            {isNotFound
              ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
              : "The FlowStack server returned an error."}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!project.data) return null;

  return (
    <div className="space-y-6">
      <div>
        <p className="label-micro text-muted-foreground">
          <Link to="/projects" className="hover:underline">
            Projects
          </Link>{" "}
          /{" "}
          <Link to={`/projects/${project.data.slug}`} className="hover:underline">
            {project.data.slug}
          </Link>{" "}
          / submit
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">Submit a workload</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A Tile is a single computation; a Flow chains tiles into the full AFFDO pipeline (or a
          custom template).
        </p>
      </div>

      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {CHOICES.map(({ slugPath, Icon, title, label, tagline, body, cost }) => (
          <li key={slugPath} className="flex">
            <Link
              to={`/projects/${project.data.slug}/submit/${slugPath}`}
              aria-label={label}
              // The card recipe arrives as a CLASS STRING so this element stays
              // an `<a>` with its own hover and focus treatment — the escape
              // hatch `card-adoption.test.tsx` names holders of, and the same
              // shape `FlowLaunchPanel`'s template cards take. It used to be a
              // `<Link>` WRAPPING a `<Card>`, which is a third pattern: the
              // surface and the control were two elements, so the focus ring
              // sat on one and the border on the other.
              className={cn(
                cardVariants({ elevation: "raised" }),
                "group flex flex-1 flex-col gap-2 p-card outline-none transition-colors",
                "hover:border-border-strong hover:bg-surface-2",
                "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              )}
            >
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
                <h2 className="font-display text-base font-semibold tracking-tight text-foreground">
                  {title}
                </h2>
              </div>
              <p className="text-sm text-muted-foreground">{tagline}</p>
              <p className="text-sm text-muted-foreground">{body}</p>
              <p className="text-xs text-muted-foreground">{cost}</p>
              {/* The affordance half of flowstack#1397. An accessible name
                  closes the WCAG failure; it does not make two paragraphs of
                  prose look like choices. `Choose →` is the same control
                  vocabulary the project page's flow cards carry (`Configure →`),
                  so a reader who has seen one recognises the other. */}
              <span className="mt-auto inline-flex items-center gap-1 pt-1 text-[11px] font-medium text-muted-foreground group-hover:text-foreground">
                Choose
                <ArrowRight className="h-3 w-3" aria-hidden />
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <Card elevation="raised">
        <CardHeader>
          <CardTitle className="text-base">Prefer the SDK?</CardTitle>
          <CardDescription>
            Every submission the GUI builds can also be sent via the SDK. See{" "}
            <Link to="/settings" className="hover:underline">
              Settings
            </Link>{" "}
            for the CLI snippet.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}
